import { useEffect, useState } from "react";
import { RotateCcw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDebounce } from "@/hooks/use-debounce";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { PublicOfferCard } from "../../shared/PublicOfferCard";
import type { PublicOffer } from "../../shared/publicOffer.types";
import type {
  MerchantOfOfferVO,
  OfferCategoryVO,
  StorefrontOfferEntry,
} from "../types";
import { fadeUp } from "../utils";
import { motion } from "framer-motion";

const SEARCH_DEBOUNCE_MS = 500;
const ALL_CATEGORIES_VALUE = "all";
const ALL_MERCHANTS_VALUE = "all";

type OfferDiscoveryProps = {
  offers: StorefrontOfferEntry[];
  loading: boolean;
  totalOfferCount: number;
  pageNumber: number;
  totalPages: number;
  categories: OfferCategoryVO[];
  selectedCategoryId: number | null;
  onCategoryChange: (categoryId: number | null) => void;
  merchants: MerchantOfOfferVO[];
  selectedMerchantId: number | null;
  onMerchantChange: (merchantId: number | null) => void;
  onPageChange: (page: number) => void;
  onOpenDetail: (offer: PublicOffer) => void;
  onSearchTitleChange: (title: string) => void;
};

export function OfferDiscovery({
  offers,
  loading,
  totalOfferCount,
  pageNumber,
  totalPages,
  categories,
  selectedCategoryId,
  onCategoryChange,
  merchants,
  selectedMerchantId,
  onMerchantChange,
  onPageChange,
  onOpenDetail,
  onSearchTitleChange,
}: OfferDiscoveryProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    onSearchTitleChange(debouncedSearch.trim());
  }, [debouncedSearch, onSearchTitleChange]);

  const handleResetFilters = () => {
    setSearch("");
    onSearchTitleChange("");
    onCategoryChange(null);
    onMerchantChange(null);
    onPageChange(0);
  };

  return (
    <section id="offers" className="bg-white px-4 py-16 sm:px-6 lg:px-8">
      <motion.div
        className="mx-auto max-w-screen-2xl"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
      >
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Discover Your Next Great Deal
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-600">
            Search through live offers from our rewards catalog.
          </p>
        </div>

        <div className="mt-10 border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <Input
                type="search"
                placeholder="Search offers by title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 border-slate-200 bg-white pl-10"
                aria-label="Search offers by title"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Select
                value={
                  selectedCategoryId != null
                    ? String(selectedCategoryId)
                    : ALL_CATEGORIES_VALUE
                }
                onValueChange={(value) => {
                  onCategoryChange(
                    value === ALL_CATEGORIES_VALUE ? null : Number(value),
                  );
                }}
              >
                <SelectTrigger
                  className="h-11 w-full min-w-[180px] border-slate-200 bg-white sm:w-[220px]"
                  aria-label="Filter by category"
                >
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_CATEGORIES_VALUE}>
                    All categories
                  </SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={String(category.id)}>
                      {category.categoryName || category.categoryCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={
                  selectedMerchantId != null
                    ? String(selectedMerchantId)
                    : ALL_MERCHANTS_VALUE
                }
                onValueChange={(value) => {
                  onMerchantChange(
                    value === ALL_MERCHANTS_VALUE ? null : Number(value),
                  );
                }}
              >
                <SelectTrigger
                  className="h-11 w-full min-w-[180px] border-slate-200 bg-white sm:w-[220px]"
                  aria-label="Filter by merchant"
                >
                  <SelectValue placeholder="All merchants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_MERCHANTS_VALUE}>
                    All merchants
                  </SelectItem>
                  {merchants.map((merchant) => (
                    <SelectItem key={merchant.id} value={String(merchant.id)}>
                      {merchant.merchantName || merchant.merchantCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="button"
                variant="outline"
                className="h-11 border-slate-200 bg-white px-4"
                onClick={handleResetFilters}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="mt-12 py-16 text-center text-sm text-slate-600">
            Loading offers…
          </p>
        ) : (
          <>
            <p className="mt-6 text-sm text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-800">
                {offers.length}
              </span>
              {totalOfferCount > 0 ? (
                <>
                  {" "}
                  of{" "}
                  <span className="font-semibold text-slate-800">
                    {totalOfferCount}
                  </span>
                </>
              ) : null}{" "}
              {totalOfferCount === 1 ? "offer" : "offers"}
            </p>

            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {offers.map((entry) => (
                <PublicOfferCard
                  key={entry.offer.id}
                  offer={entry.offer}
                  onOpenDetail={onOpenDetail}
                />
              ))}
            </div>

            <EnhancedPaginationControl
              totalPages={totalPages}
              currentPage={pageNumber}
              onPageChange={onPageChange}
              className="mt-8"
            />

            {offers.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-12 border border-dashed border-slate-300 bg-white py-16 text-center"
              >
                <p className="text-lg font-medium text-slate-700">
                  No offers match your search
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  Try a different title or clear your filters
                </p>
              </motion.div>
            )}
          </>
        )}
      </motion.div>
    </section>
  );
}
